import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '../store';
import { useLightMode } from '../utils';
import { StaffService } from '../api/StaffAPI';
import { LOCATION_META } from '../constants';
import { Header } from '../components/Header/Header';
import s from '../styles/staff.module.css';

const ALL_LOCATIONS = Object.keys(LOCATION_META);

// Every garage means "All" — there is no difference between the two, and the
// list is long enough that naming all four reads as noise.
function locationLabel(ids) {
  if (!ids || !ids.length) return 'None';
  if (ids.length >= ALL_LOCATIONS.length) return 'All';
  return ids.map((id) => LOCATION_META[id]?.name || id).join(', ');
}

// Where a technician may be scheduled. Opens on click, commits on close: a
// request per tick would fire four times while somebody picks four garages,
// and leave the row half-saved if they changed their mind.
function LocationPicker({ member, disabled, onSave }) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(member.locationIds || []);
  const started = useRef(member.locationIds || []);
  const box = useRef(null);

  useEffect(() => {
    setPicked(member.locationIds || []);
    started.current = member.locationIds || [];
  }, [member.locationIds]);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (box.current && !box.current.contains(e.target)) close(); };
    const esc = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  });

  const close = () => {
    setOpen(false);
    const before = [...started.current].sort().join(',');
    const now = [...picked].sort().join(',');
    if (before !== now) onSave(member, picked);
  };

  const toggle = (id) => setPicked((prev) => (prev.includes(id)
    ? prev.filter((x) => x !== id)
    : prev.concat([id])));

  if (disabled) return <span>{locationLabel(member.locationIds)}</span>;

  return (
    <span className={s.locPick} ref={box}>
      <button type="button" className={s.locPickBtn}
              onClick={() => (open ? close() : setOpen(true))}>
        <span>{locationLabel(picked)}</span>
        <span className={s.locPickCaret} aria-hidden="true">▾</span>
      </button>

      {open && (
        <span className={s.locPickMenu}>
          {ALL_LOCATIONS.map((id) => (
            <button key={id} type="button" className={s.locPickItem}
                    onClick={() => toggle(id)}>
              <span className={`${s.locPickBox} ${picked.includes(id) ? s.locPickBoxOn : ''}`}>
                {picked.includes(id) ? '✓' : ''}
              </span>
              {LOCATION_META[id].name}
            </button>
          ))}
          <button type="button" className={s.locPickAll}
                  onClick={() => setPicked(picked.length >= ALL_LOCATIONS.length ? [] : [...ALL_LOCATIONS])}>
            {picked.length >= ALL_LOCATIONS.length ? 'Clear all' : 'Select all'}
          </button>
          {!picked.length && (
            <span className={s.locPickNote}>
              With none picked this follows Square&rsquo;s own assignment.
            </span>
          )}
        </span>
      )}
    </span>
  );
}

const STATUS_FILTERS = ['all', 'active', 'inactive'];

export function StaffTablePage() {
  const { user } = useAuth();
  const [lightMode, toggleLightMode] = useLightMode();
  const isAdmin = user?.role === 'admin';

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['staffs'],
    queryFn: () => StaffService.list(),
  });

  const staffs = data?.staffs || [];
  const [saveError, setSaveError] = useState('');

  const saveLocations = useCallback(async (member, locationIds) => {
    setSaveError('');
    try {
      await StaffService.setLocations(member.idStaff, locationIds);
      await refetch();
    } catch (err) {
      setSaveError(err?.error || err?.message || 'Could not save those locations');
    }
  }, [refetch]);

  const [statusFilter, setStatusFilter] = useState('active');
  const visibleStaffs = statusFilter === 'all'
    ? staffs
    : staffs.filter((m) => String(m.status || '').toLowerCase() === statusFilter);

  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');

  const handleSync = useCallback(async () => {
    setSyncing(true);
    setError('');
    try {
      await StaffService.sync();
      await refetch();
    } catch (err) {
      setError(err?.error || err?.message || 'Failed to sync from Square');
    } finally {
      setSyncing(false);
    }
  }, [refetch]);


  return (
    <div className={lightMode ? `${s.page} ${s.lightPage}` : s.page}>
      <Header lightMode={lightMode} toggleLightMode={toggleLightMode} />

      <div className={s.body} style={{ gridTemplateColumns: '1fr' }}>
        <div>
          <div className={s.tableHeaderRow}>
            <div className={s.sectionLabel} style={{ marginBottom: 0 }}>Staff ({visibleStaffs.length})</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <div className={s.filterBtns} role="group" aria-label="Filter by status">
                {STATUS_FILTERS.map((f) => (
                  <button
                    key={f}
                    className={`${s.filterBtn} ${statusFilter === f ? s.filterBtnActive : ''}`}
                    onClick={() => setStatusFilter(f)}
                  >
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
              {isAdmin && (
                <button onClick={handleSync} className={s.primaryBtn} disabled={syncing}>
                  {syncing ? 'Syncing…' : 'Sync from Square'}
                </button>
              )}
            </div>
          </div>
          {error && <div className={s.empty} style={{ color: 'var(--danger-bright)' }}>{error}</div>}
          {saveError && <div className={s.attnError}>{saveError}</div>}
          <div className={s.tableWrap}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Locations</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr><td colSpan={4} className={s.loading}>Loading…</td></tr>
                )}
                {!isLoading && visibleStaffs.length === 0 && (
                  <tr><td colSpan={4} className={s.empty}>
                    {staffs.length === 0
                      ? <>No staff yet. {isAdmin ? 'Click “Sync from Square”.' : ''}</>
                      : `No ${statusFilter === 'all' ? '' : statusFilter + ' '}staff.`}
                  </td></tr>
                )}
                {!isLoading && visibleStaffs.map((m) => (
                  <tr key={m.idStaff}>
                    <td>{`${m.given_name || ''} ${m.family_name || ''}`.trim() || '—'}</td>
                    <td>{m.email || '—'}</td>
                    <td>
                      <LocationPicker member={m} disabled={!isAdmin} onSave={saveLocations} />
                    </td>
                    <td>{m.status || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
